'use client'
import React, {useContext} from "react"
import {
  Box,
  Paper,
  Button,
  useTheme,
  Typography,
  useMediaQuery, Stack,
  Chip, Divider,
  Skeleton, alpha, Alert,
} from "@mui/material"
import Grid from "@mui/material/Grid"
import {GLOBAL_PATHS} from "@/lib/globals"
import {TranslationContext} from "@/configs/context"
import StyleIcon from '@mui/icons-material/Style'
import CheckIcon from '@mui/icons-material/Check'
import {formatNumber} from "@/lib/libs"
import LinearProgress from "@mui/material/LinearProgress"
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import {GlassTab, GlassTabList} from "@/ui/components/custom/tabs"
import {TabContext, TabPanel} from "@mui/lab"
import Image from "next/image"
import { useCardPricing } from "@/hooks/account/useCardPricing"
import { ToolPadGlassBox } from "@/ui/components/custom/box"

/**
 * Ce composant est un call to action de la page
 * @constructor
 */
const PricingWrapper: React.FC = () => {
  const theme = useTheme()
  // @ts-expect-error:next-line
  const { t }: object = useContext(TranslationContext)
  const matchMd = useMediaQuery(theme.breakpoints.up('md'))
  const matchLg = useMediaQuery(theme.breakpoints.up('lg'))
  
  const skeletonLineWith: string = "100%"

  const [selectedTab, setSelectedTab] = React.useState("0")
  
  // Utilisation du hook personnalisé
  const { orgPricing, personalPricing, isLoading, error, refetch } = useCardPricing()

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue.toString())
  }

  const pricingToShow = (isLoading ? Array.from(new Array(4)) : orgPricing)
  const sharedPaperSx = {
    px: 3, py: 0, pt: 3,
    color: "text.primary",
    textAlign: matchMd ? "left" : "center"
  }

  return (
    <Box
        id="banner-section"
        sx={{ mt: { xs: 8, md: 8 }, mb: { xs: 6, md: 8 } }}
      >
        <ToolPadGlassBox sx={{ ...sharedPaperSx }}>
          <Stack 
            direction="column" 
            alignItems="center" 
            sx={{ mb: { xs: 4, md: 6 } }}
          >
            <Typography variant="h4" align="center" sx={{ fontWeight: "bold" }}>
              {t('translation:pricing.title')}
            </Typography>
            <Typography
              align="center"
              sx={{ marginTop: "0.5rem" }}
              variant={matchMd ? "h6" : "body1"}
            >
              {t('translation:pricing.subtitle')}
            </Typography>
          </Stack>
          {error ? (
            <Alert
              variant="outlined"
              color="error"
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={refetch}>
                  {t('translation:common.retry')}
                </Button>
              }
            >
              {error}
            </Alert>
          ) : !pricingToShow ? (
            <Alert
              variant="outlined"
              color="error"
              severity="error"
              sx={{ mb: 2 }}
            >
              {t('translation:error.pricing_no_data_found')}
            </Alert>
          ) : (
            <TabContext value={selectedTab}>
              <Stack justifyItems="center">
                <GlassTabList
                  centered
                  onChange={handleTabChange}
                  aria-label="pricing tabs"
                >
                  <GlassTab value="0" label={t('translation:pricing.organization')} />
                  <GlassTab value="1" label={t('translation:pricing.personal')} />
                </GlassTabList>
              </Stack>
              <TabPanel value="0" sx={{ px: 0 }}>
                <Grid
                  container
                  spacing={2}
                  alignItems="stretch"
                  justifyContent={matchLg ? "space-between" : "center"}
                >
                  {pricingToShow.map((item, index) => {
                    return (
                      <Grid
                        key={`pricing--${index}`}
                        sx={{ textAlign: "center" }}
                        size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            width: "100%",
                            height: "100%",
                            textAlign: "left",
                            backgroundSize: "100% 100% !important",
                            backgroundRepeat: "no-repeat !important",
                            backgroundPosition: "center top !important",
                            backgroundImage: `url(${GLOBAL_PATHS.COVERS}bg-11.webp) !important`,
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              height: "100%",
                              color: "common.white",
                              backgroundColor: "rgba(0, 0, 0, 0.25) !important"
                            }}
                          >
                            {item ?
                              <Stack
                                direction="column"
                                sx={{ height: "100%" }}
                                justifyContent="space-between"
                              >
                                <Stack direction="row" justifyContent="space-between">
                                  <Box>
                                    <Typography variant="body2" align="center">
                                      <StyleIcon fontSize="large" />
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: -1, fontSize: 12 }}>
                                      {t(`translation:pricing.${item.code}.sup_title`)?.toUpperCase()}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="h6" align="right">
                                      {t(`translation:pricing.${item.code}.title`)}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      variant="filled"
                                      color="secondary"
                                      label={item.shipping}
                                      sx={{ height: 20, fontSize: "0.6rem" }}
                                    />
                                  </Box>
                                </Stack>
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body1">
                                    {t(`translation:pricing.${item.code}.body_title`)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 2 }}>
                                    {t(`translation:pricing.${item.code}.body_subtitle`)}
                                  </Typography>
                                  <Typography variant="body2" align="inherit">
                                    {t(`translation:pricing.${item.code}.content`)}
                                  </Typography>
                                  <Stack direction="column" sx={{ mt: 2, mb: 2, ml: -1 }}>
                                    {item?.list?.map((li, index) => (
                                      <Box key={`princing-opt-${item.code}-${index}`} sx={{ mt: .25 }}>
                                        <Box sx={{ mb: .25, display: "flex", gap: "0.5rem" }}>
                                          <CheckIcon fontSize="small" />
                                          <Typography variant="body2">
                                            {t(`translation:pricing.${item.code}.list_item_${index+1}`)}
                                          </Typography>
                                        </Box>
                                        {item.list.length-1 > index ?
                                          <Divider sx={{ backgroundColor: alpha("#ffffff", 0.5) }} /> : null}
                                      </Box>
                                    ))}
                                  </Stack>
                                </Box>
                                {item.card ?
                                  <Typography variant="body1" align="inherit">
                                    {item.card}&nbsp;
                                    {t('translation:pricing.cards', {count: parseInt(item.card)})} /&nbsp;
                                    <b>{formatNumber(item.packPrice)} {t('translation:pricing.xaf')}</b>
                                  </Typography> :
                                  <Box>
                                    <Typography variant="body1" align="inherit">
                                      {t('translation:pricing.more_than_50_cards')}
                                    </Typography>
                                  </Box>}
                                <Button
                                  fullWidth
                                  size="large"
                                  variant="glass"
                                  href="#contact-section"
                                  className="FluidScroll-anchor"
                                  startIcon={<ShoppingCartIcon />}
                                  sx={{ mt: 1, background: theme.palette.common.black }}
                                >
                                  {t(`translation:pricing.${item.card ? "order" : "contact_us"}`)}
                                </Button>
                              </Stack>
                              :
                              <Box sx={{ width: "100%" }}>
                                <Stack direction="row" justifyContent="space-between" gap={4}>
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Skeleton variant="circular" width={24} height={24} sx={{ ml: 1.5 }} />
                                    <Skeleton variant="text" sx={{ mb: .5, width: 48, fontSize: '0.7rem' }} />
                                  </Box>
                                  <Box sx={{ ml: 1, flexGrow: 2 }}>
                                    <Skeleton variant="text" sx={{ mb: .5, fontSize: '1rem' }} />
                                    <Skeleton variant="text" sx={{ mb: .5, fontSize: '0.7rem' }} />
                                  </Box>
                                </Stack>
                                <Skeleton
                                  component="div"
                                  variant="rectangular"
                                  width={skeletonLineWith} height={48}
                                  sx={{ mt: 2, mb: 3 }}
                                />
                                <Skeleton
                                  component="div"
                                  variant="rectangular"
                                  width={skeletonLineWith} height={64}
                                  sx={{ mt: 2, mb: 3 }}
                                />
                                <Skeleton
                                  component="div"
                                  variant="rectangular"
                                  width={skeletonLineWith} height={80}
                                  sx={{ mt: 2, mb: 3 }}
                                />
                                <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                                <Skeleton
                                  component="div"
                                  variant="rectangular"
                                  width={skeletonLineWith} height={24}
                                  sx={{ mt: 1 }}
                                />
                              </Box>}
                          </Paper>
                        </Paper>
                      </Grid>
                    )
                  })}
                </Grid>
              </TabPanel>
              <TabPanel value="1" sx={{ px: 0 }}>
                <Paper
                  elevation={0}
                  sx={{
                    ...sharedPaperSx,
                    py: 2, pb: 3,
                    backgroundSize: `${matchMd ? '100% 100%' : "default"} !important`,
                    backgroundRepeat: "no-repeat !important",
                    backgroundPosition: "center top !important",
                    backgroundImage: `url(${GLOBAL_PATHS.COVERS}bg-2.jpg) !important`,
                  }}
                >
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{  minHeight: 400 }}
                    justifyContent={matchLg ? "space-between" : "center"}
                  >
                    <Grid size={{ xs: 12, sm: 12, md: 4, lg: 3, xl: 3 }}>
                      <Grid
                        container
                        spacing={2}
                        alignItems="stretch"
                        justifyContent={matchLg ? "space-between" : "center"}
                      >
                        {personalPricing.map((item, index) => {
                          return (
                            <Grid
                              key={`pricing--${index}`}
                              sx={{ textAlign: "center" }}
                              size={{ xs: 12 }}
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  textAlign: "left",
                                  backgroundSize: "100% 100% !important",
                                  backgroundRepeat: "no-repeat !important",
                                  backgroundPosition: "center top !important",
                                  backgroundColor: "rgba(0, 0, 0, 0.25) !important",
                                  backgroundImage: `url(${GLOBAL_PATHS.COVERS}bg-11.webp) !important`,
                                }}
                              >
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    height: "100%",
                                    color: "common.white",
                                    backgroundColor: "rgba(0, 0, 0, 0.25) !important"
                                  }}
                                >
                                  {item ?
                                    <Stack
                                      direction="column"
                                      sx={{ height: "100%" }}
                                      justifyContent="space-between"
                                    >
                                      <Stack direction="row" justifyContent="space-between">
                                        <Box>
                                          <Typography variant="body2" align="center">
                                            <StyleIcon fontSize="large" />
                                          </Typography>
                                          <Typography variant="body2" sx={{ mt: -1, fontSize: 12 }}>
                                            {t(`translation:pricing.${item.code}.sup_title`)?.toUpperCase()}
                                          </Typography>
                                        </Box>
                                        <Box>
                                          <Typography variant="h6" align="right">
                                            {t(`translation:pricing.${item.code}.title`)}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            variant="filled"
                                            color="secondary"
                                            label={item.shipping}
                                            sx={{ height: 20, fontSize: "0.6rem" }}
                                          />
                                        </Box>
                                      </Stack>
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="body1">
                                          {t(`translation:pricing.${item.code}.body_title`)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2 }}>
                                          {t(`translation:pricing.${item.code}.body_subtitle`)}
                                        </Typography>
                                        <Typography variant="body2" align="inherit">
                                          {t(`translation:pricing.${item.code}.content`)}
                                        </Typography>
                                        <Stack direction="column" sx={{ mt: 2, mb: 2, ml: -1 }}>
                                          {item?.list?.map((li, index) => (
                                            <Box key={`princing-opt-${item.code}-${index}`} sx={{ mt: .25 }}>
                                              <Box sx={{ mb: .25, display: "flex", gap: "0.5rem" }}>
                                                <CheckIcon fontSize="small" />
                                                <Typography variant="body2">
                                                  {t(`translation:pricing.${item.code}.list_item_${index+1}`)}
                                                </Typography>
                                              </Box>
                                              {item.list.length-1 > index ?
                                                <Divider sx={{ backgroundColor: alpha("#ffffff", 0.5) }} /> : null}
                                            </Box>
                                          ))}
                                        </Stack>
                                      </Box>
                                      {item.card ?
                                        <Typography variant="body1" align="inherit">
                                          {item.card}&nbsp;
                                          {t('translation:pricing.cards', {count: parseInt(item.card)})} /&nbsp;
                                          <b>{formatNumber(item.packPrice)} {t('translation:pricing.xaf')}</b>
                                        </Typography> :
                                        <Box>
                                          <Typography variant="body1" align="inherit">
                                            {t('translation:pricing.more_than_50_cards')}
                                          </Typography>
                                        </Box>}
                                      <Button
                                        fullWidth
                                        size="large"
                                        variant="glass"
                                        href="#contact-section"
                                        className="FluidScroll-anchor"
                                        startIcon={<ShoppingCartIcon />}
                                        sx={{ mt: 1, background: theme.palette.common.black }}
                                      >
                                        {t(`translation:pricing.${item.card ? "order" : "contact_us"}`)}
                                      </Button>
                                    </Stack>
                                    :
                                    <Box sx={{ width: "100%" }}>
                                      <Stack direction="row" justifyContent="space-between" gap={4}>
                                        <Box sx={{ flexGrow: 1 }}>
                                          <Skeleton variant="circular" width={24} height={24} sx={{ ml: 1.5 }} />
                                          <Skeleton variant="text" sx={{ mb: .5, width: 48, fontSize: '0.7rem' }} />
                                        </Box>
                                        <Box sx={{ ml: 1, flexGrow: 2 }}>
                                          <Skeleton variant="text" sx={{ mb: .5, fontSize: '1rem' }} />
                                          <Skeleton variant="text" sx={{ mb: .5, fontSize: '0.7rem' }} />
                                        </Box>
                                      </Stack>
                                      <Skeleton
                                        component="div"
                                        variant="rectangular"
                                        width={skeletonLineWith} height={48}
                                        sx={{ mt: 2, mb: 3 }}
                                      />
                                      <Skeleton
                                        component="div"
                                        variant="rectangular"
                                        width={skeletonLineWith} height={64}
                                        sx={{ mt: 2, mb: 3 }}
                                      />
                                      <Skeleton
                                        component="div"
                                        variant="rectangular"
                                        width={skeletonLineWith} height={80}
                                        sx={{ mt: 2, mb: 3 }}
                                      />
                                      <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                                      <Skeleton
                                        component="div"
                                        variant="rectangular"
                                        width={skeletonLineWith} height={24}
                                        sx={{ mt: 1 }}
                                      />
                                    </Box>}
                                </Paper>
                              </Paper>
                            </Grid>
                          )
                        })}
                      </Grid>
                    </Grid>
                    <Grid
                      sx={{ textAlign: "center", color: "common.white" }}
                      size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 7 }}
                    >
                      <Image
                        alt=""
                        width={250}
                        height={250}
                        src={`${GLOBAL_PATHS.IMAGES}8306360.webp`}
                      />
                      <Typography 
                        variant="h4" 
                        sx={{ fontWeight: "bold" }}
                      >
                        {t('translation:pricing.title')}
                      </Typography>
                      <Typography
                        sx={{ marginTop: "0.5rem" }}
                        variant="body1"
                      >
                        {t('translation:pricing.subtitle')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </TabPanel>
            </TabContext>
          )}
        </ToolPadGlassBox>
      </Box>
  )
}

export default PricingWrapper
